import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useSocket, useQueueUpdates, useNowPlaying, useSongDone, useGigEvents } from '../contexts/SocketContext';
import { useIsMobile } from '../hooks/useIsMobile';

interface CurrentSong {
  id: number;
  title: string;
  artist: string;
  user_name: string;
  pdf_path: string | null;
  slug?: string;
  chart_type?: string;
  fallback?: boolean;
  lyrics?: string;
}

interface QueueItem {
  id: number;
  title: string;
  artist: string;
  user_name: string;
  status: string;
}

interface Gig {
  id: number;
  name: string;
  status: string;
  started_at: string;
}

const INSTRUMENT_LABELS: Record<string, string> = {
  bass: 'Bass',
  guitar1: 'Guitar 1',
  guitar2: 'Guitar 2',
  keys: 'Keys',
  drums: 'Drums',
  fiddle: 'Fiddle / Mandolin',
  vocals: 'Vocals',
  other: 'Other'
};

const BandDashboardPage: React.FC = () => {
  const { member, logout } = useAuth();
  const { joinRoom } = useSocket();
  const navigate = useNavigate();
  const [currentSong, setCurrentSong] = useState<CurrentSong | null>(null);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [currentGig, setCurrentGig] = useState<Gig | null>(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  // Join the band room for socket updates
  useEffect(() => {
    joinRoom('band');
  }, [joinRoom]);

  // Note: Auth redirect is handled by ProtectedRoute in App.tsx

  const fetchData = useCallback(async () => {
    try {
      // Fetch current song with instrument-specific chart
      const instrument = member?.instrument || 'full';
      const songRes = await apiCall(`/api/songs/current-song-chart?instrument=${instrument}`);
      if (songRes.ok) {
        const songData = await songRes.json();
        setCurrentSong(songData);
      } else {
        setCurrentSong(null);
      }

      // Fetch queue
      const queueRes = await apiCall('/api/songs/queue');
      if (queueRes.ok) {
        const queueData = await queueRes.json();
        setQueue(queueData);
      }

      // Fetch current gig
      const gigRes = await apiCall('/api/songs/gigs/current');
      if (gigRes.ok) {
        const gigData = await gigRes.json();
        setCurrentGig(gigData);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [member?.instrument]);

  // Initial fetch and polling fallback
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Real-time updates
  useQueueUpdates(useCallback(() => fetchData(), [fetchData]));
  useNowPlaying(useCallback(() => fetchData(), [fetchData]));
  useSongDone(useCallback(() => fetchData(), [fetchData]));
  useGigEvents(
    useCallback(() => fetchData(), [fetchData]),
    useCallback(() => fetchData(), [fetchData])
  );

  // Build chart URL - now the API returns the correct path with fallback already applied
  const getChartUrl = () => {
    if (!currentSong?.pdf_path) return null;
    const baseUrl = process.env.REACT_APP_API_URL || '';
    return `${baseUrl}${currentSong.pdf_path}#navpanes=0&toolbar=0`;
  };

  const isVocalist = member?.instrument === 'vocals';

  const queuedSongs = queue.filter(item => item.status === 'queued');
  const nextUp = queuedSongs.slice(0, 3);

  if (!member) {
    return null;
  }

  return (
    <div style={{ minHeight: '100vh', background: '#1a0f0a', color: '#f5deb3', padding: isMobile ? 16 : 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? 20 : 32, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: isMobile ? '1.5rem' : '2rem' }}>Band Dashboard</h1>
          <p style={{ margin: '8px 0 0', opacity: 0.8, fontSize: isMobile ? '0.9rem' : undefined }}>
            Welcome, {member.displayName} ({INSTRUMENT_LABELS[member.instrument]})
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {currentGig && (
            <span style={{ background: '#ffd700', color: '#1a0f0a', padding: '6px 12px', borderRadius: 20, fontWeight: 600, fontSize: isMobile ? '0.85rem' : undefined }}>
              {currentGig.name}
            </span>
          )}
          <button
            onClick={logout}
            style={{
              padding: '8px 16px',
              background: '#e74c3c',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer'
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <p style={{ fontSize: '1.5rem' }}>Loading...</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 16 : 24 }}>
          {/* Now Playing */}
          <div style={{ background: '#28190f', borderRadius: 12, padding: 24, border: '1px solid #5a3a1a' }}>
            <h2 style={{ color: '#ffd700', marginTop: 0 }}>Now Playing</h2>
            {currentSong ? (
              <div>
                <h3 style={{ fontSize: '1.8rem', margin: '0 0 8px' }}>{currentSong.title}</h3>
                <p style={{ fontSize: '1.2rem', opacity: 0.8, margin: '0 0 16px' }}>
                  {currentSong.artist} - sung by {currentSong.user_name}
                </p>
                {currentSong.fallback && (
                  <p style={{ fontSize: '0.9rem', color: '#c8850f', margin: '0 0 12px' }}>
                    No {INSTRUMENT_LABELS[member.instrument]} chart - showing Full Band chart
                  </p>
                )}
                {!isVocalist && currentSong.pdf_path && (
                  <a
                    href={getChartUrl() || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      padding: '12px 24px',
                      background: '#8b4513',
                      color: '#f5deb3',
                      textDecoration: 'none',
                      borderRadius: 6,
                      fontWeight: 600
                    }}
                  >
                    View {currentSong.fallback ? 'Full Band' : INSTRUMENT_LABELS[member.instrument]} Chart
                  </a>
                )}
                {isVocalist && (
                  <p style={{ fontSize: '1rem', opacity: 0.8, fontStyle: 'italic' }}>
                    Lyrics shown below
                  </p>
                )}
              </div>
            ) : (
              <p style={{ opacity: 0.6 }}>No song currently playing</p>
            )}
          </div>

          {/* Up Next */}
          <div style={{ background: '#28190f', borderRadius: 12, padding: 24, border: '1px solid #5a3a1a' }}>
            <h2 style={{ color: '#c8850f', marginTop: 0 }}>Up Next</h2>
            {nextUp.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {nextUp.map((item, index) => (
                  <div
                    key={item.id}
                    style={{
                      padding: 16,
                      background: index === 0 ? '#3a2415' : '#1e1008',
                      borderRadius: 8,
                      borderLeft: index === 0 ? '4px solid #c8850f' : 'none'
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: index === 0 ? '1.2rem' : '1rem' }}>
                      {item.title}
                    </div>
                    <div style={{ opacity: 0.7, marginTop: 4 }}>
                      {item.artist} - {item.user_name}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ opacity: 0.6 }}>No songs in queue</p>
            )}
          </div>

          {/* Chart or Lyrics View */}
          {currentSong && (
            <div style={{ gridColumn: '1 / -1', background: '#28190f', borderRadius: 12, padding: 24, border: '1px solid #5a3a1a' }}>
              {isVocalist ? (
                <>
                  <h2 style={{ marginTop: 0 }}>Lyrics</h2>
                  {currentSong.lyrics ? (
                    <pre style={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'Georgia, serif',
                      fontSize: '1.2rem',
                      lineHeight: 1.8,
                      color: '#f5deb3',
                      background: '#1e1008',
                      padding: 24,
                      borderRadius: 8,
                      maxHeight: isMobile ? 400 : 600,
                      overflow: 'auto'
                    }}>
                      {currentSong.lyrics}
                    </pre>
                  ) : (
                    <p style={{ opacity: 0.6, fontStyle: 'italic' }}>No lyrics available for this song</p>
                  )}
                </>
              ) : currentSong.pdf_path ? (
                <>
                  <h2 style={{ marginTop: 0 }}>
                    {currentSong.fallback ? 'Full Band Chart' : `${INSTRUMENT_LABELS[member.instrument]} Chart`}
                  </h2>
                  <iframe
                    src={getChartUrl() || ''}
                    style={{
                      width: '100%',
                      height: isMobile ? 400 : 600,
                      border: 'none',
                      borderRadius: 8,
                      background: 'white'
                    }}
                    title="Current Song Chart"
                  />
                </>
              ) : (
                <>
                  <h2 style={{ marginTop: 0 }}>Chart</h2>
                  <p style={{ opacity: 0.6, fontStyle: 'italic' }}>No chart available for this song</p>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BandDashboardPage;
